import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/** Named slots of the v03 shell. Coordinates and layers live only in shell.css. */
export type SlotName = 'scene' | 'crest' | 'quest' | 'window' | 'chat' | 'combat-chat' | 'hud' | 'system'

/**
 * default — scene with journal sheet; window — journal collapses to a line;
 * combat — HUD gives way to the combat console, sections hidden, chat is a line;
 * dialog — portrait and conversation window with its own buttons.
 */
export type ShellMode = 'default' | 'window' | 'combat' | 'dialog'

export type SystemKind = 'loading' | 'transition' | 'error'
export type SystemMessage = {
  kind: SystemKind
  text: string
  /** Bold part after the text, e.g. a destination. */
  detail?: string
  onRetry?: () => void
}

export type ShellLayoutKind = 'scene' | 'window'

type Ctx = {
  mode: ShellMode | null
  setMode: (mode: ShellMode | null) => void
  layout: ShellLayoutKind
  setLayout: (layout: ShellLayoutKind) => void
  slot: (name: SlotName) => HTMLElement | null
  registerSlot: (name: SlotName, node: HTMLElement | null) => void
  pushSystem: (message: SystemMessage) => symbol
  dropSystem: (id: symbol) => void
  system: SystemMessage | null
}

const ShellContext = createContext<Ctx | null>(null)

const PRIORITY: Record<SystemKind, number> = { error: 3, transition: 2, loading: 1 }

export function ShellProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ShellMode | null>(null)
  const [layout, setLayout] = useState<ShellLayoutKind>('window')
  const [slots, setSlots] = useState<Partial<Record<SlotName, HTMLElement | null>>>({})
  const [messages, setMessages] = useState<Array<{ id: symbol; message: SystemMessage }>>([])

  const registerSlot = useCallback((name: SlotName, node: HTMLElement | null) => {
    setSlots(prev => (prev[name] === node ? prev : { ...prev, [name]: node }))
  }, [])
  const pushSystem = useCallback((message: SystemMessage) => {
    const id = Symbol(message.kind)
    setMessages(prev => [...prev, { id, message }])
    return id
  }, [])
  const dropSystem = useCallback((id: symbol) => setMessages(prev => prev.filter(m => m.id !== id)), [])

  // The most important message wins; among equals the newest.
  const system = useMemo(() => {
    let best: SystemMessage | null = null
    for (const { message } of messages) if (!best || PRIORITY[message.kind] >= PRIORITY[best.kind]) best = message
    return best
  }, [messages])

  const value = useMemo<Ctx>(() => ({
    mode, setMode, layout, setLayout,
    slot: name => slots[name] ?? null,
    registerSlot, pushSystem, dropSystem, system,
  }), [mode, layout, slots, registerSlot, pushSystem, dropSystem, system])

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>
}

export function useShell() {
  const ctx = useContext(ShellContext)
  if (!ctx) throw new Error('useShell outside the game shell')
  return ctx
}

/** Optional access for components that also render outside the v03 shell. */
export function useShellOptional() {
  return useContext(ShellContext)
}

/** A screen sets the shell mode (and whether it fills the scene or a window) while mounted. */
export function useShellMode(mode: ShellMode, layout: ShellLayoutKind = mode === 'default' ? 'scene' : 'window') {
  const ctx = useShellOptional()
  const setMode = ctx?.setMode
  const setLayout = ctx?.setLayout
  useEffect(() => {
    if (!setMode || !setLayout) return
    setMode(mode)
    setLayout(layout)
    return () => setMode(null)
  }, [setMode, setLayout, mode, layout])
}

/** Shows a system strip message while `message` is not null. */
export function useSystemMessage(message: SystemMessage | null) {
  const ctx = useShellOptional()
  const push = ctx?.pushSystem
  const drop = ctx?.dropSystem
  const latest = useRef(message)
  latest.current = message
  const key = message ? `${message.kind}|${message.text}|${message.detail ?? ''}|${message.onRetry ? 1 : 0}` : ''
  useEffect(() => {
    if (!push || !drop || !latest.current) return
    const current = latest.current
    const id = push({ ...current, onRetry: current.onRetry ? () => latest.current?.onRetry?.() : undefined })
    return () => drop(id)
  }, [push, drop, key])
}

/** Renders children into a named slot of the shell (no-op outside it). */
export function ShellSlot({ name, children }: { name: SlotName; children: ReactNode }) {
  const ctx = useShellOptional()
  const node = ctx?.slot(name)
  if (!node) return null
  return createPortal(children, node)
}

/** Ref callback registering a slot container. */
export function useSlotRef(name: SlotName) {
  const { registerSlot } = useShell()
  return useCallback((node: HTMLElement | null) => registerSlot(name, node), [registerSlot, name])
}
